import { fieldToTextField, TextFieldProps } from 'formik-mui';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { OkvedInfoDto } from '@sr/dto';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { isArray } from 'class-validator';
import { useOkvedDropdown } from './hook';

export const OkvedPickerField = (props: TextFieldProps) => {
  const {
    form: { setFieldValue },
    field: { name, value },
    label
  } = props;

  const [open, setOpen] = useState(false);
  const { items: list, isLoading: loading } = useOkvedDropdown();
  const [selectedCities, setSelectedCities] = useState<OkvedInfoDto[]>([]);
  const [containerHeight, setContainerHeight] = useState('auto');

  const onChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, newValue: OkvedInfoDto[]) => {
      setSelectedCities(newValue);
      setFieldValue(name, newValue.map(x => x.id));
      
      setTimeout(() => {
        const input = document.querySelector(`[name="${name}"]`)?.closest('.MuiAutocomplete-root');
        if (input) {
          const scrollHeight = input.scrollHeight;
          setContainerHeight(`${Math.max(56, scrollHeight)}px`);
        }
      }, 0);
    },
    [setFieldValue, name]
  );

  useEffect(() => {
    if (!list.length) return;
    if (!isArray(value)) return;
    
    const selected = value
      .map((x: number) => list.find((c: OkvedInfoDto) => c.id === x))
      .filter(x => x !== undefined) as OkvedInfoDto[];
    setSelectedCities(selected);
    
    setTimeout(() => {
      const input = document.querySelector(`[name="${name}"]`)?.closest('.MuiAutocomplete-root');
      if (input) {
        const scrollHeight = input.scrollHeight;
        setContainerHeight(`${Math.max(56, scrollHeight)}px`);
      }
    }, 100);
  }, [list, value, name]);

  const isLoading = open && list.length === 0 && loading;

  return (
    <Autocomplete<OkvedInfoDto, true, false>
      value={selectedCities}
      autoComplete={false}
      multiple
      filterSelectedOptions
      options={list}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={onChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => `${option.code} - ${option.name}`}
      loading={isLoading}
      loadingText={'Загрузка...'}
      noOptionsText={'Ничего не найдено'}
      sx={{
        '& .MuiAutocomplete-root': {
          height: containerHeight,
          transition: 'height 0.2s ease-in-out',
        },
        '& .MuiAutocomplete-inputRoot': {
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          padding: '8px',
          minHeight: '56px',
          height: '100%',
        },
        '& .MuiAutocomplete-input': {
          flex: 1,
          minWidth: '50px',
        },
        '& .MuiChip-root': {
          margin: '2px',
          height: '32px',
          '& .MuiChip-label': {
            whiteSpace: 'normal',
            overflow: 'visible',
          },
        },
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={`${option.code} - ${option.name}`}
            size="medium"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...fieldToTextField(props)}
          {...params}
          name={name}
          label={label}
          placeholder="Начните набирать..."
          sx={{
            '& .MuiInputBase-root': {
              height: '100%',
              alignItems: 'flex-start',
            },
          }}
        />
      )}
    />
  );
};